import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import * as z from "zod";
import { CACHE_OWNER_STORAGE_KEY } from "@/components/sidebar/useCodesmithEnabledCache";
import type { AssistantSessionsPoolResponse } from "@/lib/assistant-sessions";
import { authHadSession } from "@/lib/authHadSession";
import { buildSha } from "@/lib/buildInfo";
import type { CodesmithProject } from "@/lib/codesmith-projects/types";
import { namedOrgKey, orgStorageKey, type NamedOrg } from "@/lib/named-org";
import { safeLocalStorage } from "@/lib/storage";
import type { AutomationSessionGroup } from "./types";
export const SIDEBAR_CACHE_STORAGE_KEY_PREFIX = "codesmith-sidebar-cache:";
// An expanded list past a few pages runs to hundreds of KB; past this the
// entry is dropped rather than crowding chat drafts out of the quota.
const MAX_ENTRY_BYTES = 1024 * 1024;
const SessionsPoolShape = z.object({
  sessions: z.array(z.object({
    id: z.string()
  })),
  total: z.number(),
  has_more: z.boolean()
});
const SessionsPool = z.custom<AssistantSessionsPoolResponse>(value => SessionsPoolShape.safeParse(value).success);
const CachedSessions = z.object({
  mine: SessionsPool,
  collaborating: SessionsPool,
  team: SessionsPool,
  archived: SessionsPool
});
const ProjectShape = z.object({
  id: z.string(),
  hidden: z.boolean()
});
const CachedProjects = z.array(z.custom<CodesmithProject>(value => ProjectShape.safeParse(value).success));
const AutomationGroupShape = z.object({
  automation_id: z.string(),
  recent_runs: z.array(z.unknown())
});
const CachedAutomationGroups = z.array(z.custom<AutomationSessionGroup>(value => AutomationGroupShape.safeParse(value).success));

// Entries hold API payloads, whose shape can change with a deploy; one
// written by another build is dropped instead of trusted.
const Envelope = z.object({
  build: z.string().nullable(),
  value: z.unknown()
});

/**
 * While auth is pending the cache's owner stands in for the user, as long as
 * this browser's last session is still the one loading: the sign-in page,
 * logout and session expiry clear authHadSession, so a different account
 * never loads with it set, and the user who resolves clears a cache that
 * isn't theirs (clearCodesmithLayoutCache).
 */
function ownedBy(userId: number | undefined): boolean {
  const owner = safeLocalStorage.get(CACHE_OWNER_STORAGE_KEY);
  if (owner === null) return false;
  return userId === undefined ? authHadSession.read() !== false : owner === String(userId);
}
interface SidebarCacheEntry<T> {
  /** The org's value from last visit; `userId` is undefined while auth is pending. */
  read(org: NamedOrg, userId: number | undefined): T | undefined;
  /** Saves a live value; skipped until the owner marker names `userId`. */
  write(org: NamedOrg, userId: number | undefined, value: T): void;
}
function sidebarCacheEntry<T>(kind: string, schema: z.ZodType<T>): SidebarCacheEntry<T> {
  const storageKey = (org: NamedOrg) => orgStorageKey(`${SIDEBAR_CACHE_STORAGE_KEY_PREFIX}${kind}:`, org);
  return {
    read(org: NamedOrg, userId: number | undefined): T | undefined {
      if (!ownedBy(userId)) return undefined;
      const raw = safeLocalStorage.get(storageKey(org));
      if (raw === null) return undefined;
      let json: unknown;
      try {
        json = JSON.parse(raw);
      } catch {
        return undefined;
      }
      const envelope = Envelope.safeParse(json);
      if (!envelope.success || envelope.data.build !== buildSha()) {
        return undefined;
      }
      const value = schema.safeParse(envelope.data.value);
      return value.success ? value.data : undefined;
    },
    write(org: NamedOrg, userId: number | undefined, value: T): void {
      if (userId === undefined || !ownedBy(userId)) return;
      const key = storageKey(org);
      const result = safeLocalStorage.set(key, JSON.stringify({
        build: buildSha(),
        value
      }), {
        maxBytes: MAX_ENTRY_BYTES
      });
      if (!result.ok) safeLocalStorage.remove(key);
    }
  };
}

/**
 * `initialDataUpdatedAt` for a query seeded from last visit: stale, so the
 * query still fetches, but not 0, which react-query v3 reads as never updated
 * and resets to `loading` (no data) while that fetch runs.
 */
export const LAST_VISIT_UPDATED_AT = 1;

/** Whether a query's data is still last visit's (or absent). */
export function isLastVisitData(dataUpdatedAt: number): boolean {
  return dataUpdatedAt <= LAST_VISIT_UPDATED_AT;
}

/** Last visit's sidebar data per org, shown until the live reads land. */
export const sidebarCache = {
  sessions: sidebarCacheEntry("sessions", CachedSessions),
  projects: sidebarCacheEntry("projects", CachedProjects),
  automationGroups: sidebarCacheEntry("automation-groups", CachedAutomationGroups)
};

/** Whether a list has a row the landing view would open. */
export function hasSessionRows(sessions: z.infer<typeof CachedSessions>) {
  return [sessions.mine, sessions.collaborating, sessions.team].some(pool => pool.sessions.length > 0);
}

/** Whether last visit's list for the org has a row the landing view would open. */
export function hasCachedSidebarSessions(org: NamedOrg, userId: number | undefined): boolean {
  const sessions = sidebarCache.sessions.read(org, userId);
  return sessions !== undefined && hasSessionRows(sessions);
}

/**
 * The mount's org's value from last visit, read once; undefined for any
 * other org, so a query keyed on the new org isn't seeded with it.
 */
export function useLastVisit(entry, org, userId, t0) {
  const $ = _c(8);
  const shouldRead = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== org) {
    t1 = namedOrgKey(org);
    $[0] = org;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const orgKey = t1;
  let t2;
  if ($[2] !== entry || $[3] !== org || $[4] !== orgKey || $[5] !== shouldRead || $[6] !== userId) {
    t2 = () => ({
      orgKey,
      value: org && shouldRead ? entry.read(org, userId) : undefined
    });
    $[2] = entry;
    $[3] = org;
    $[4] = orgKey;
    $[5] = shouldRead;
    $[6] = userId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const [lastVisit] = useState(t2);
  return lastVisit.orgKey === orgKey ? lastVisit.value : undefined;
}